/**
 * Centralized Role Configuration
 * This file contains all role-related constants and helper functions
 */

// Role Types
export type UserRole =
  | 'HOTEL_ADMIN'
  | 'HOTEL_BRAND'
  | 'ALO_SUPER'
  | 'ENTERPRISE_ADMIN'
  | 'INTEGRATIONS_ADMIN'
  | string;

// Individual Role Constants
export const ROLES = {
  ALO_SUPER: 'ALO_SUPER',
  HOTEL_ADMIN: 'HOTEL_ADMIN',
  HOTEL_BRAND: 'HOTEL_BRAND',
  ENTERPRISE_ADMIN: 'ENTERPRISE_ADMIN',
  INTEGRATIONS_ADMIN: 'INTEGRATIONS_ADMIN',
  BRAND_ADMIN: 'BRAND_ADMIN',
} as const;

/**
 * Role Groups
 * Add new roles to these arrays to automatically update behavior across the app
 */

// Roles that represent hotel-level users (not super admin)
// These users see the Hotels page as their dashboard, have limited permissions
export const HOTEL_ROLES: readonly string[] = [
  ROLES.HOTEL_ADMIN,
  ROLES.HOTEL_BRAND,
  // Add new hotel-level roles here
] as const;

// Roles that should see admin-specific features
export const ADMIN_ROLES: readonly string[] = [ROLES.ALO_SUPER] as const;

/**
 * Helper Functions
 */

/**
 * Check if a user has a hotel-level role (not super admin)
 * @param role - The user's role
 * @returns true if user has a hotel role
 */
export const isHotelRole = (role?: string): boolean => {
  if (!role) return false;
  return HOTEL_ROLES.includes(role);
};

/**
 * Check if a user is a super admin
 * @param role - The user's role
 * @returns true if user is ALO_SUPER
 */
export const isSuperAdmin = (role?: string): boolean => {
  return role === ROLES.ALO_SUPER;
};

/**
 * Check if a user is NOT a super admin (hotel roles)
 * @param role - The user's role
 * @returns true if user is not ALO_SUPER
 */
export const isNotSuperAdmin = (role?: string): boolean => {
  return !isSuperAdmin(role);
};

/**
 * Check if a user is an enterprise admin
 * @param role - The user's role
 * @returns true if user is ENTERPRISE_ADMIN
 */
export const isEnterpriseAdmin = (role?: string): boolean => {
  return role === ROLES.ENTERPRISE_ADMIN;
};

/**
 * Check if a user is an brand admin
 * @param role - The user's role
 * @returns true if user is BRAND_ADMIN
 */
export const isBrandAdmin = (role?: string): boolean => {
  return role === ROLES.BRAND_ADMIN;
};

export const isHotelAdmin = (role?: string): boolean => {
  return role === ROLES.HOTEL_ADMIN;
};

/**
 * Check if a user is an integrations admin
 * @param role - The user's role
 * @returns true if user is INTEGRATIONS_ADMIN
 */
export const isIntegrationsAdmin = (role?: string): boolean => {
  return role === ROLES.INTEGRATIONS_ADMIN;
};

/**
 * Format role name for display
 * @param role - The role to format
 * @returns Formatted role name
 */
export const formatUserRole = (role?: string): string => {
  if (!role) return 'Unknown';

  switch (role) {
    case ROLES.ALO_SUPER:
      return 'Super Admin';
    case ROLES.HOTEL_ADMIN:
      return 'Hotel Admin';
    case ROLES.HOTEL_BRAND:
      return 'Hotel Brand';
    default:
      // Convert SNAKE_CASE to Title Case
      return role
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
  }
};
